// ============================================================
// requestIntent — 依頼の種類を見て、AI のやりすぎ・言いすぎを止める（2026-10-31 予約セッション3041）
//
// 起きたこと（TR-2TC95Y-3BBKHB・前田さん「決済の状況を確認して下さい」を5回）:
//   1. get_square_payment_status を一度も呼ばなかった。古い画面のときに3回「Square を確認する道具がない」と答え、
//      その発言が会話の履歴に残ったまま新しい画面で再開したため、AI は自分の過去の発言に合わせて呼ばなかった。
//      呼ばずに終わるのを止める仕組みも無かった
//   2. 確認の依頼なのに、受信メールを archive_mail した（Gmail の受信トレイからも外れた）・下書きを作った
//   3. Square を見ていないのに「決済状況を確認いたしました」。下書き 846 に「新しい請求書を発行しました」（発行していない）
//   4. 下書き 849 に「当日カウンターでの支払いは受けられない」と方針を断定した（現地払いの可否は前田さんが決める）
//
// ここは判定だけ（純粋関数）。止める・差し戻すのは useAgent。
//
// 2026-10-31（2）版 1138 でも同じ会話で効かなかった:
//   - 開きっぱなしの古い画面で会話が続き、画面側の判定がそもそも動いていなかった
//     → 決済の確認での Square の強制・古い「道具がない」の打ち消しはサーバ（sys/agent/chat_request.php）へ。
//       判定を持たない古い画面の呼び出しはサーバが断る（AGENT_CLIENT_GUARDS）
//   - 新しい画面でも「最後の依頼文」が1つ前の発言だった（画面の一覧は描画後に追いつくため）
//     → 会話の履歴（contents）から取る（lastOperatorText）
// ============================================================

import { isWriteTool } from './taskPlan';
import type { GeminiContent } from './types';

/**
 * この画面が持つ判定の世代。サーバ（sys/agent/chat_request.php の AGENT_MIN_CLIENT_GUARDS）は、
 * これより古い画面（送ってこない画面）からの予約エージェントの呼び出しを断り、再読み込みを案内する。
 * 確認だけの依頼の書き込み停止など、画面側でしか効かない判定を足したら上げる（サーバ側も揃えて上げる）
 */
export const AGENT_CLIENT_GUARDS = 2;   // 2026-11-03: 固定ルール #26（下書きの合計の照合・送信済みカード・内訳）

/**
 * オペレータの最後の発言（【システム】の添え書き・ツールの結果は除く）。
 * 画面の一覧（uiMessages）は描画のあとに追いつくので、送った直後のループで読むと1つ前の発言になる。
 * 会話の履歴は送る前に積むので、こちらから取る（2026-10-31 セッション3041）
 */
export function lastOperatorText(contents: GeminiContent[]): string {
  for (let i = (contents ?? []).length - 1; i >= 0; i--) {
    const c: any = contents[i];
    if (c?.role !== 'user') continue;
    const p = (c.parts ?? []).find((x: any) => typeof x?.text === 'string' && x.text.trim() && !x.text.trim().startsWith('【システム】'));
    if (p) return String(p.text).trim();
  }
  return '';
}

const norm = (s: string) => String(s ?? '').replace(/\s+/g, '');

/** 決済・支払いの状況の確認依頼か（「決済の状況を確認して」「支払いは済んでる？」「Square を見て」など） */
export function isPaymentStatusRequest(text: string): boolean {
  const t = norm(text);
  if (!t) return false;
  const topic = /(決済|支払|入金|お支払|Square|スクエア|square|請求書|払われ|払え)/i.test(t);
  const ask = /(状況|状態|確認|チェック|見て|調べ|済ん|済み|できて|できた|どうな|払われ|入って|届いて|払え|されて|された)/.test(t);
  return topic && ask;
}

/**
 * 確認だけの依頼か（状態を変える操作を頼んでいない）。
 * 「確認して」「状況を教えて」は確認だけ。「確認して返信して」「確認してアーカイブ」は操作も頼んでいる。
 */
export function isCheckOnlyRequest(text: string): boolean {
  const t = norm(text);
  if (!t) return false;
  if (!/(確認|状況|状態|教えて|どうなって|見て|調べ|チェック|済んで|済み[？?]|できて)/.test(t)) return false;
  return !/(返信|起案|下書|送信|送って|送る|送り|アーカイブ|取り?消|取消|作成|作って|作り?直|再発行|発行して|変更|変えて|キャンセル|追加|削除|割り?当て|反映|オンに|オフに|メモ|案内して|対応して|処理して|進めて|直して)/.test(t);
}

/** 状態を変える操作（確認だけの依頼では呼ばない）。tier B に加えて、アーカイブ・フォローアップ・起案 */
const STATE_CHANGING_A = new Set(['archive_mail', 'set_mail_followup', 'draft_reservation_email']);
export function isStateChangingTool(tool: string): boolean {
  return isWriteTool(tool) || STATE_CHANGING_A.has(tool);
}

/** 確認だけの依頼で状態を変えようとしたら止める（止めるときは AI に返す文） */
export function checkOnlyGuard(tool: string, lastUserText: string): string | null {
  if (!isStateChangingTool(tool) || !isCheckOnlyRequest(lastUserText)) return null;
  return `この依頼は確認だけです（「${String(lastUserText).trim().slice(0, 40)}」）。${tool} は状態を変えるので実行していません。`
    + '調べた結果だけを伝え、メールの起案・アーカイブ・予約の変更などが要りそうなら、するかどうかをオペレータに聞いてください。';
}

/** 決済の確認依頼のターンに添える【システム】の一文 */
export function paymentStatusHint(contactCode: string): string {
  const code = String(contactCode ?? '').trim();
  return '【システム】決済・支払いの状況の確認依頼。最初に get_square_payment_status'
    + (code ? `(contact_code: "${code}")` : '（予約番号）') + ' を呼び、返ってきた状態・金額・支払い済み額・決済の試みをそのまま伝える。'
    + 'この会話の前のほうで「Square を確認する道具がない」と言っていても、それは古い画面のときの話で、今はこのツールがある。'
    + '確認だけの依頼なので、メールの起案・アーカイブ・予約や請求書の変更はしない。';
}

/** 「確認しました」系の発言（決済・Square について）。否定（確認できません等）は除く */
const PAYMENT_CHECK_CLAIM = /(決済|支払|入金|Square|スクエア)[^。\n]{0,20}(確認(いた)?しました|確認できました|確認したところ|確認いたしました|確認済み)/i;
/**
 * このターンに get_square_payment_status が成功していないのに、AI が決済を確認したと言った・決済の確認依頼に答えずに終わった。
 * 差し戻しの文を返す（null なら問題なし）
 */
export function paymentStatusNudge(text: string, lastUserText: string, succeededTools: string[]): string | null {
  if (succeededTools.includes('get_square_payment_status')) return null;
  const claimed = PAYMENT_CHECK_CLAIM.test(String(text ?? ''));
  if (!claimed && !isPaymentStatusRequest(lastUserText)) return null;
  return '【システム】'
    + (claimed ? 'いまの返答は決済を確認したと言っているが、このターンで get_square_payment_status を実行していない（確認していない）。'
      : 'オペレータは決済の状況の確認を頼んでいるが、get_square_payment_status を呼ばずに答えた。')
    + 'いま get_square_payment_status を呼んで、その結果だけを伝えること。呼べないなら「Square はまだ確認していない」とはっきり書くこと。'
    + '「道具がない」と答えない（このツールがある）。';
}

/** 新しい請求書を発行・送付したという記述 */
const INVOICE_ISSUED_CLAIM = new RegExp([
  // 2026-10-31 下書き 846「we have issued a new Square invoice」（間に Square などの語が入る）
  String.raw`(have|has|we've|I've)\s+(just\s+)?(sent|issued|created|re-?issued|generated)\s+(you\s+)?(a\s+|the\s+)?(new|another|updated|fresh)?\s*(\w+\s+)?invoice`,
  String.raw`(new|updated)\s+invoice\s+(has\s+been|was|is)\s+(sent|issued|created)`,
  String.raw`請求書[^。\n]{0,12}(再発行|発行|送付|お送り|作成|再送)(いた)?し(ました|ております)`,
].join('|'), 'i');

/** 現地（カウンター・当日・現金）での支払いの可否・事前決済の要否を述べる記述（方針はオペレータが決める） */
const ONSITE_PAYMENT_POLICY = new RegExp([
  String.raw`(cannot|can't|can\s*not|unable\s+to|do\s+not|don't|not\s+able\s+to|are\s+able\s+to|can)\s+(accept|take|receive|process)[^.\n]{0,80}(cash|on-?site|counter|in\s+person|upon\s+arrival|on\s+arrival|at\s+pick-?up)`,
  String.raw`(pay|payment)[^.\n]{0,40}(at\s+the\s+counter|on-?site|upon\s+arrival|on\s+arrival|in\s+cash|at\s+pick-?up)`,
  String.raw`(require|requires|required|must)[^.\n]{0,40}(advance|prepaid|pre-?payment|online\s+payment)`,
  String.raw`(現地|カウンター|窓口|店頭|当日)[^。\n]{0,15}(払|決済|精算)`,
  String.raw`現金[^。\n]{0,10}(払|決済|精算|受け)`,
  String.raw`事前(決済|支払|のお支払)[^。\n]{0,15}(必須|原則|のみ|必要|お願い)`,
].join('|'), 'i');

/** オペレータが現地払いの方針を今のターンで言ったか（言っていればその方針を書いてよい） */
function operatorStatedPaymentPolicy(text: string): boolean {
  return /(現地|カウンター|窓口|店頭|当日払|当日の支払|現金|counter|cash|on-?site)/i.test(String(text ?? ''));
}

/**
 * お客様向け下書きの本文に、事実でない・決めていないことが書かれていないか。
 * - 新しい請求書を発行・送付したと書いたのに、この会話で create_square_invoice が成功していない
 * - 現地払いの可否・事前決済の要否を、オペレータが言っていないのに断定している
 */
export function findDraftClaimProblems(body: string, ctx: { invoiceCreated: boolean; lastUserText: string }): string[] {
  const b = String(body ?? '');
  const problems: string[] = [];
  if (!ctx.invoiceCreated && INVOICE_ISSUED_CLAIM.test(b)) {
    problems.push('「新しい請求書を発行・送付した」と書いているが、この会話で請求書を作っていない（create_square_invoice が成功していない）。事実でないので書かない');
  }
  if (ONSITE_PAYMENT_POLICY.test(b) && !operatorStatedPaymentPolicy(ctx.lastUserText)) {
    problems.push('現地（カウンター・当日・現金）での支払いの可否や、事前決済が必要かを書いている。これはオペレータ（前田さん）が決めることで、AI が決めて書いてはいけない。書く前にオペレータに方針を聞く');
  }
  return problems;
}

/** 下書きの起案を止めるときに AI へ返す文（null なら止めない） */
export function draftClaimGuard(tool: string, args: Record<string, any>, ctx: { invoiceCreated: boolean; lastUserText: string }): string | null {
  if (tool !== 'draft_reservation_email') return null;
  const problems = findDraftClaimProblems(`${args?.body ?? ''}\n${args?.instruction ?? ''}`, ctx);
  if (!problems.length) return null;
  return `下書きを作っていません。${problems.join('。')}。オペレータに確認してから起案し直してください。`;
}

/**
 * 保存された会話に「◯◯を確認する道具がない」という AI の発言が残っているか。
 * 残っていれば、開き直したときに「今の道具の一覧で判断する」と添える（古い画面の発言に引きずられないように）
 */
const NO_TOOL_CLAIM = /(ツール|道具|機能|仕組み)[^。\n]{0,10}(を持っておりません|を持っていない|が(備わって|用意されて)い(ない|ません)|がない|がありません|はありません)|直接(照会|確認|アクセス)[^。\n]{0,20}(できません|機能を持って)/;
export function hasStaleNoToolClaim(contents: Array<{ role?: string; parts?: any[] }>): boolean {
  return (contents ?? []).some((c) => c?.role === 'model'
    && (c.parts ?? []).some((p: any) => typeof p?.text === 'string' && NO_TOOL_CLAIM.test(p.text)));
}
export const STALE_NO_TOOL_NOTE = '【システム】この会話の前のほうで AI が「◯◯を確認する道具（ツール）がない」と答えているが、それは当時の画面の話。'
  + '今使える道具は今のツール一覧で判断すること（例: Square の決済状況は get_square_payment_status で確認できる）。';

/**
 * オペレータへの返答で、店の方針（事前決済の原則・現地払いの可否など）を言い切っているか。
 * 2026-10-31 セッション3041:「当店の規定では事前オンライン決済をお願いしております」「オンライン決済（事前決済）の原則に基づき」
 */
const REPLY_POLICY_CLAIM = new RegExp([
  String.raw`(規定|原則|ポリシー|方針|ルール|決まり)[^。\n]{0,25}(事前|オンライン|現地|カウンター|窓口|店頭|当日|現金)[^。\n]{0,10}(決済|払|精算)`,
  String.raw`(事前|オンライン|現地|カウンター|窓口|店頭|当日|現金)[^。\n]{0,10}(決済|払|精算)[^。\n]{0,15}(の)?(規定|原則|ポリシー|方針|決まり)`,
  String.raw`(現地|カウンター|窓口|店頭|当日)[^。\n]{0,12}(払|決済|精算)[^。\n]{0,10}(はできません|はできない|は受け付けて(い|お)りません|不可|お断り)`,
].join('|'));
/** オペレータに聞いている文（「方針についてご指示を」「現地払いを受けますか？」）は言い切りではない */
const ASKS_OPERATOR = /(ご指示|ご判断|伺|しますか|ますか[？?]|でしょうか|いかが|どうしますか|教えてください|お知らせください)/;
export function hasReplyPolicyClaim(text: string): boolean {
  return String(text ?? '').split(/[。\n]/).some((sentence) => REPLY_POLICY_CLAIM.test(sentence) && !ASKS_OPERATOR.test(sentence));
}
export function replyPolicyNudge(text: string, lastUserText: string): string | null {
  if (!hasReplyPolicyClaim(text) || operatorStatedPaymentPolicy(lastUserText)) return null;
  return '【システム】いまの返答は、店の方針（事前決済が原則・現地払いを受けない など）を言い切っているが、'
    + 'オペレータはそう指示していない（方針は前田さんが決める）。方針として書かず、「現地払いを受けますか？」とオペレータに聞くこと。'
    + '確認の依頼には、調べた事実だけを答え直すこと。';
}

/**
 * 「計算します」「少々お待ちください」と言って、ツールを呼ばずにターンを終えた（2026-11-03 N3）。
 * 10/31 セッション3089「料金・在庫一覧を取得します。」、11/02 セッション3229「再計算いたします。少々お待ちください。」で止まり、
 * スタッフがもう一度頼み直した。最後の文がこれからやる宣言なら1回差し戻す
 */
const STALLED_PROMISE = /(取得|確認|計算|再計算|検索|調査|調べ|作成|起案|翻訳|お調べ|照会)(いた|致)?し(ます|てまいります)[。！!]?\s*(少々お待ち(ください|下さい)[。！!]?)?\s*$|少々お待ち(ください|下さい)[。！!]?\s*$/;
export function stalledPromiseNudge(text: string): string | null {
  const t = String(text ?? '').trim();
  if (!t || !STALLED_PROMISE.test(t)) return null;
  return '【システム】いまの返答は「〜します・少々お待ちください」で終わっているが、ツールを呼んでいない（何も実行されていない）。'
    + 'いま必要なツールを呼んで、その結果を伝えること。呼ばないなら「まだ実行していない」と書くこと。';
}

/** システムプロンプトに入れるルール */
export function buildRequestIntentPrompt(): string {
  return [
    '# 確認の依頼と、事実・方針の書き方（2026-10-31 予約セッション3041）',
    '- 「決済の状況を確認して」「支払いは済んでる？」「Square を見て」など決済・支払いの確認は、**必ず get_square_payment_status を呼んで**その結果を伝える。',
    '  メールを読んで推測しない。「道具がない」と答えない。',
    '- **確認だけの依頼では、状態を変えない**（メールの起案・アーカイブ・フォローアップ・予約や請求書の変更をしない）。',
    '  要りそうな操作は「〜しますか？」とオペレータに聞く。',
    '- **道具で確かめていないことを、確かめた・やったと書かない**。お客様向けの文にも、まだしていないこと（請求書の発行・送付など）を書かない。',
    '- **方針を作らない**。現地（カウンター・当日・現金）での支払いを受けるか、事前決済が必要かなどは前田さんが決める。',
    '  お客様に聞かれたら、答えを書く前に「現地払いを受けますか？」とオペレータに聞き、その答えのとおりに起案する。',
    '- **日時を推測しない**: 時刻が無い日付（「10月31日から」）は、今の時刻を当てはめずに「何時からですか？」と聞く。',
    '  見積もりの一覧だけなら 12:00 の仮定でよいが、そう書く。**予約を作るときは必ず時刻を確かめる**（「今から」と言われたときだけ今の時刻）。',
    '- 「〜します」「少々お待ちください」と書いたら、**同じターンでそのツールを呼ぶ**（宣言だけで終えない）。',
  ].join('\n');
}
